import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { useParams, useLocation } from 'react-router-dom';
import isClient from 'commons/isClient';

import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import SuspenseWithLoader from '../SuspenseWithLoader';
import PDPPlaceholder from '../PDP/PDPPlaceholder';
import ProductListingPagePlaceholder from '../ProductListingPage/ProductListingPagePlaceholder';

const specialCategories = {
  'mid-century-furniture': true,
  'small-space-furniture': true,
  modern: true,
  new: true,
};

const CategoryPage = loadable(
  () => import('../CategoryPage'),
  isNewConsumerApp
    ? { loading: ProductListingPagePlaceholder }
    : {
        fallback: <div />,
      }
);

const PDP = loadable(
  () => import('../PDP'),
  isNewConsumerApp
    ? { loading: PDPPlaceholder }
    : {
        fallback: <PDPPlaceholder />,
      }
);

const ProductAndCategory = props => {
  const { category: categoryFromPath, slug: slugFromPath } = useParams();
  const location = useLocation();

  const {
    category: categoryOverride,
    slug: slugOverride,
    isAdmin,
    productOptionsPromise,
  } = props;

  const category = categoryOverride || categoryFromPath;
  const slug = slugOverride || slugFromPath;

  // Create a key based on slug + search params
  // This ensures PDP remounts when navigating from category page with different fabrics
  // The key changes when location.search changes, causing remount
  // Available In Sizes widget uses router state with resetState: false, so it preserves state
  const pdpKey = useMemo(() => {
    // Don't change anything in the old consumer app. Here, the key is always the slug.
    if (!isNewConsumerApp) {
      return slug;
    }
    const search = isClient() ? window.location.search : location.search;
    return search ? `${slug}${search}` : slug;
  }, [slug, location.search]);

  if (isAdmin) {
    return (
      <AdminPlaceholder>
        ProductAndCategory Component Placeholder for Variant Builder. <br />
        The component will display the product page correctly in Consumer
      </AdminPlaceholder>
    );
  }

  if (specialCategories[category]) {
    const categoryPageSlug = `${category}/${slug}`;

    return (
      <SuspenseWithLoader
        fallback={<ProductListingPagePlaceholder />}
        key={categoryPageSlug}
      >
        <CategoryPage slug={categoryPageSlug} key={categoryPageSlug} />
      </SuspenseWithLoader>
    );
  }

  return (
    <SuspenseWithLoader fallback={<PDPPlaceholder />} key={pdpKey}>
      <PDP key={pdpKey} productOptionsPromise={productOptionsPromise} />
    </SuspenseWithLoader>
  );
};

ProductAndCategory.propTypes = {
  isAdmin: PropTypes.bool,
  category: PropTypes.string,
  slug: PropTypes.string,
  productOptionsPromise: PropTypes.object,
};

export default ProductAndCategory;
